import { useRef, useState } from "react";
import { parsePurchaseOrderWrite } from "../infrastructure/api/purchase-order-read-contracts";
import type { Dispatch, SetStateAction } from "react";
import { remainingQuantity, type PurchaseOrder, type PurchaseOrderCandidate } from "../domain/models";
import { purchaseOrderGateway } from "../infrastructure/api/purchase-order-gateway";
import type { PurchaseOrderDialog as Dialog, PurchaseOrdersProps } from "./purchase-order-workspace-types";

type Props = {
  scope: { isCurrent: () => boolean };
  notify: PurchaseOrdersProps["notify"];
  isRolePreview: boolean;
  replaceOrder: (order: PurchaseOrder) => void;
  setCandidates: Dispatch<SetStateAction<PurchaseOrderCandidate[]>>;
  onCreated: (order: PurchaseOrder) => void;
  onReceived: (order: PurchaseOrder) => void;
};

export function usePurchaseOrderForm({ scope, notify, isRolePreview, replaceOrder, setCandidates, onCreated, onReceived }: Props) {
  const [dialog, updateDialog] = useState<Dialog>(null);

  const [submitting, setSubmitting] = useState(false);
  const [expectedQuantity, setExpectedQuantity] = useState("");
  const [quantity, setQuantity] = useState("");
  const [returnQuantity, setReturnQuantity] = useState("0");
  const [returnDisposition, setReturnDisposition] = useState<
    "REPLACE" | "DEDUCT_FROM_ORDER"
  >("REPLACE");
  const [deliveredAt, setDeliveredAt] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [qualityStatus, setQualityStatus] = useState<
    "PENDING" | "ACCEPTED" | "CONDITIONAL" | "REJECTED"
  >("PENDING");
  const [qualityNote, setQualityNote] = useState("");
  const [qualityDisposition, setQualityDisposition] = useState<
    "REPLACE" | "DEDUCT_FROM_ORDER" | "CONDITIONAL_ACCEPTANCE" | "CREATE_CAPA"
  >("CREATE_CAPA");
  const [returnReason, setReturnReason] = useState("");
  const [note, setNote] = useState("");
  const [deliveryNoteNo, setDeliveryNoteNo] = useState("");
  const [deliveryDocument, setDeliveryDocument] = useState<File | null>(null);
  const [dispatchRecipient, setDispatchRecipient] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");

  const [operationError, setOperationError] = useState("");
  const createKeysRef = useRef(new Map<string, string>());
  const receiptKeysRef = useRef(new Map<string, string>());

  const busy = useRef(false);
  const currentDialog = useRef(dialog);
  currentDialog.current = dialog;
  const setDialog = (next: Dialog) => {
    currentDialog.current = next;
    updateDialog(next);
  };

  const execute = async (
    operation: () => Promise<PurchaseOrder>,
    success: string,
  ) => {
    if (!scope.isCurrent() || busy.current || !dialog || currentDialog.current !== dialog) return;
    const target = dialog;
    busy.current = true;
    setSubmitting(true);
    setOperationError("");
    try {
      const updated = parsePurchaseOrderWrite(await operation(), target);
      if (!scope.isCurrent()) return;
      replaceOrder(updated);
      if (target.kind === "create") {
        setCandidates((current) =>
          current.filter(
            (item) => item.requestNo !== target.candidate.requestNo,
          ),
        );
        createKeysRef.current.delete(target.candidate.requestNo);
        if (currentDialog.current === target) onCreated(updated);
      }
      if (target.kind === "receive") {
        receiptKeysRef.current.delete(target.order.id);
        if (currentDialog.current === target) onReceived(updated);
      }
      if (currentDialog.current === target) {
        setDialog(null);
        setNote("");
        setQualityNote("");
        setQualityDisposition("CREATE_CAPA");
        setReturnReason("");
        setReturnDisposition("REPLACE");
        setDeliveryNoteNo("");
        setDeliveryDocument(null);
      }
      notify(success, "success");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "İşlem tamamlanamadı.";
      if (!scope.isCurrent()) return;
      if (currentDialog.current === target) setOperationError(message);
      notify(message, "error");
    } finally {
      busy.current = false;
      if (scope.isCurrent()) setSubmitting(false);
    }
  };

  const openReceipt = (order: PurchaseOrder) => {
    const remaining = remainingQuantity(order);
    setExpectedQuantity(String(remaining));
    setQuantity(String(remaining));
    setReturnQuantity("0");
    setReturnDisposition("REPLACE");
    setQualityStatus("PENDING");
    setQualityNote("");
    setReturnReason("");
    setNote("");
    setDeliveryNoteNo("");
    setDeliveryDocument(null);
    setDialog({ kind: "receive", order });
  };

  const chooseDeliveryDocument = (file: File | undefined) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      notify("İrsaliye belgesi en fazla 20 MB olabilir.", "error");
      return;
    }
    if (!["application/pdf", "image/png", "image/jpeg"].includes(file.type)) {
      notify(
        "İrsaliye yalnızca PDF, PNG veya JPEG olarak yüklenebilir.",
        "error",
      );
      return;
    }
    setDeliveryDocument(file);
  };

  const expected = Number(expectedQuantity);
  const received = Number(quantity);
  const returned = Number(returnQuantity);
  const canSubmit = Boolean(dialog) && !submitting && (
    dialog?.kind === "create" ||
    (dialog?.kind === "send" && Boolean(dispatchRecipient.trim()) && deliveryAddress.trim().length >= 5) ||
    (dialog?.kind === "receive" &&
      Number.isFinite(expected) && expected > 0 && expected <= remainingQuantity(dialog.order) &&
      Number.isFinite(received) && received > 0 && received <= expected &&
      Number.isFinite(returned) && returned >= 0 && returned <= received &&
      (returned === 0 || Boolean(returnReason.trim())) && Boolean(deliveryNoteNo.trim()) && Boolean(deliveryDocument)) ||
    (dialog?.kind === "quality" && qualityStatus !== "PENDING" && (qualityStatus === "ACCEPTED" || Boolean(qualityNote.trim()))) ||
    (dialog?.kind === "cancel" && note.trim().length >= 5)
  );

  const submit = () => {
    if (!canSubmit || !dialog || currentDialog.current !== dialog || busy.current || !scope.isCurrent()) return;
    if (dialog.kind === "create") {
      const key =
        createKeysRef.current.get(dialog.candidate.requestNo) ??
        crypto.randomUUID();
      createKeysRef.current.set(dialog.candidate.requestNo, key);
      void execute(
        () =>
          purchaseOrderGateway.create(
            dialog.candidate.requestNo,
            key,
          ),
        "Sipariş taslağı oluşturuldu.",
      );
    }
    if (dialog.kind === "send")
      void execute(
        () =>
          purchaseOrderGateway.send(dialog.order.id, {
            deliveryAddress: deliveryAddress.trim(),
          }),
        isRolePreview
          ? "Demo gönderimi tamamlandı; gerçek e-posta gönderilmedi."
          : "Sipariş e-postası kuyruğa alındı; sağlayıcı doğrulaması bekleniyor.",
      );
    if (dialog.kind === "receive" && deliveryDocument) {
      const key =
        receiptKeysRef.current.get(dialog.order.id) ??
        crypto.randomUUID();
      receiptKeysRef.current.set(dialog.order.id, key);
      void execute(
        () =>
          purchaseOrderGateway.receive(dialog.order.id, {
            quantity: Number(quantity),
            expectedQuantity: Number(expectedQuantity),
            returnQuantity: Number(returnQuantity),
            returnDisposition:
              Number(returnQuantity) > 0
                ? returnDisposition
                : undefined,
            deliveredAt,
            returnReason: returnReason.trim() || undefined,
            receiptNote: note.trim() || undefined,
            deliveryNoteNo: deliveryNoteNo.trim(),
            document: deliveryDocument,
            idempotencyKey: key,
          }),
        "Teslimat kaydedildi. Kayıt Kalite Kontrol Kuyruğu’na gönderildi.",
      );
    }
    if (dialog.kind === "quality" && qualityStatus !== "PENDING")
      void execute(
        () =>
          purchaseOrderGateway.reviewQuality(
            dialog.order.id,
            dialog.deliveryId,
            {
              qualityStatus,
              qualityNote: qualityNote.trim() || undefined,
              rejectionDisposition:
                qualityStatus === "REJECTED"
                  ? qualityDisposition
                  : undefined,
            },
          ),
        qualityStatus === "REJECTED" &&
          qualityDisposition === "CREATE_CAPA"
          ? "Kalite sonucu kaydedildi ve DÖF oluşturuldu."
          : "Kalite sonucu kaydedildi.",
      );
    if (dialog.kind === "cancel")
      void execute(
        () =>
          purchaseOrderGateway.cancel(dialog.order.id, note.trim()),
        "Sipariş iptal edildi.",
      );
  };
  return {
    dialog,
    submitting,
    expectedQuantity,
    setExpectedQuantity,
    quantity,
    setQuantity,
    returnQuantity,
    setReturnQuantity,
    returnDisposition,
    setReturnDisposition,
    deliveredAt,
    setDeliveredAt,
    qualityStatus,
    setQualityStatus,
    qualityNote,
    setQualityNote,
    qualityDisposition,
    setQualityDisposition,
    returnReason,
    setReturnReason,
    note,
    setNote,
    deliveryNoteNo,
    setDeliveryNoteNo,
    deliveryDocument,
    setDeliveryDocument,
    dispatchRecipient,
    setDispatchRecipient,
    deliveryAddress,
    setDeliveryAddress,
    operationError,
    setOperationError,
    setDialog,
    openReceipt,
    chooseDeliveryDocument,
    canSubmit,
    submit,
  };
}
